import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DELIVERY_TYPES, SNAKE_DELIVERY_TYPES } from '~constants/delivery';
import { DISPATCH_METHODS, ORDER_STATUS, PAYMENT_METHODS } from '~constants/orders';

import { MERCAT_CHANNEL, TOOLTIP_KEYS } from './constants';

export const useDeliveryStatus = (
  orderStatus,
  dispatchMethod,
  deliveries,
  channel,
  paymentMethod,
  isRejected,
  hasDelivery
) => {
  const isPreparing = orderStatus === ORDER_STATUS.PREPARING;
  const isMercatChannel = !channel || channel === MERCAT_CHANNEL;
  const { deliveryMethods } = useSelector((state) => state.stores.storeById) || {};
  const isReady = orderStatus === ORDER_STATUS.READY;

  const deliveryError = useMemo(
    () =>
      isPreparing &&
      isMercatChannel &&
      (dispatchMethod === DISPATCH_METHODS.store_delivery || dispatchMethod === DISPATCH_METHODS.delivery) &&
      !hasDelivery,
    [dispatchMethod, hasDelivery, isMercatChannel, isPreparing]
  );

  const isDelivery = [DISPATCH_METHODS.store_delivery, DISPATCH_METHODS.delivery].includes(dispatchMethod);
  const isNotReadyForDelivery =
    deliveries?.[0]?.type === DELIVERY_TYPES.OWN && !isReady && paymentMethod === PAYMENT_METHODS.ONLINE;
  const hasOtherEnabledDeliveryMethod = deliveryMethods?.some(
    (method) => method.enabled && method.deliveryType !== SNAKE_DELIVERY_TYPES.OwnDelivery
  );

  const disableDeliveryActions = useMemo(() => {
    return !(
      isMercatChannel &&
      isDelivery &&
      (!hasDelivery || isRejected || isNotReadyForDelivery) &&
      hasOtherEnabledDeliveryMethod
    );
  }, [
    isMercatChannel,
    isDelivery,
    hasDelivery,
    isRejected,
    isNotReadyForDelivery,
    hasOtherEnabledDeliveryMethod
  ]);

  const deliveryTooltip = useMemo(() => {
    if (dispatchMethod === DISPATCH_METHODS.in_store) {
      return TOOLTIP_KEYS.pickUpDelivery;
    }

    if (paymentMethod === PAYMENT_METHODS.CARD && hasDelivery && !isRejected) {
      return TOOLTIP_KEYS.cardPayed;
    }

    if (paymentMethod === PAYMENT_METHODS.CASH && hasDelivery && !isRejected) {
      return TOOLTIP_KEYS.cashPayed;
    }

    if ((!hasDelivery || isRejected) && dispatchMethod === DISPATCH_METHODS.store_delivery) {
      return TOOLTIP_KEYS.assignDelivery;
    }

    if (hasDelivery && !isRejected && deliveries[0].type !== DELIVERY_TYPES.OWN) {
      return TOOLTIP_KEYS.externalDelivery;
    }

    if (
      hasDelivery &&
      deliveries[0].type === DELIVERY_TYPES.OWN &&
      !deliveryMethods?.some(
        (method) => method.enabled && method.deliveryType !== SNAKE_DELIVERY_TYPES.OwnDelivery
      )
    ) {
      return TOOLTIP_KEYS.noAlternatives;
    }

    if (
      hasDelivery &&
      deliveries[0].type === DELIVERY_TYPES.OWN &&
      dispatchMethod === DISPATCH_METHODS.store_delivery &&
      paymentMethod === PAYMENT_METHODS.ONLINE
    ) {
      return TOOLTIP_KEYS.changeDelivery;
    }
  }, [deliveries, deliveryMethods, dispatchMethod, hasDelivery, isRejected, paymentMethod]);

  return {
    deliveryError,
    disableDeliveryActions,
    deliveryTooltip,
    isMercatChannel
  };
};
